import i18next from 'i18next';
import Dom from '../../helpers/dom-helper';
import { isMobileBrowser } from '../../helpers/browser-detector.helper';
import Storage from '../../helpers/storage';
import modalTemplate from './consent-modal.html';

const MODAL_TEMPLATE = modalTemplate();
const BODY_CONSENT_MODAL_CLASSNAME = 'consent-modal-open';
const STORAGE_KEY_CONSENT_HIDDEN = 'privacy-modal-hidden';

const ONE_WEEK_IN_MS = 604800000;

export const CONSENT_TYPE_OPTIONAL = 'optional';
export const CONSENT_TYPE_MANDATORY = 'mandatory';

const TRANSLATION_NAMESPACE_V1 = 'privacy-policy';
const TRANSLATION_NAMESPACE_V2 = 'privacy-policy-v2';

// v2 links directly to the cookie overview so recipients do not have to hunt for it
// inside the privacy notice. Required for German GDPR compliance (FEAT-9657).
export const COOKIE_INFO_URL =
    'https://www.showpad.com/privacy-principles#2-do-the-products-and-services-use-cookies-or-similar-technology';

// Only absolute http(s) URLs may be rendered as links. The privacy policy URL comes from the
// share info payload, so a `javascript:`/`data:` value must never reach an anchor's href.
const ALLOWED_LINK_PROTOCOLS = ['http:', 'https:'];

// Both versions render the same set of paragraphs; only the copy behind the keys differs.
const allowedTranslationKeys = [
    'content-sharing-purpose',
    'cookie-tracking',
    'account-tracking',
    'privacy-policy-reference',
    'consent-revoke',
    'option',
];

export default class ConsentModal {
    constructor(
        container,
        domManager,
        eventBus,
        consentType,
        privacyPolicyUrl,
        onConsentAccepted,
        onViewContent,
        consentExpiry,
        useConsentV2 = false,
    ) {
        this.consentType = consentType;
        this.privacyPolicyUrl = privacyPolicyUrl;
        this.container = container;
        this.domManager = domManager;
        this.eventBus = eventBus;
        this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onViewContentButtonClick = this.onViewContentButtonClick.bind(this);
        this.onViewContent = onViewContent;
        this.onConsentAccepted = onConsentAccepted;
        this.isOpen = false;

        this.consentExpiry = consentExpiry;
        this.useConsentV2 = useConsentV2;
        this.translationNamespace = useConsentV2 ? TRANSLATION_NAMESPACE_V2 : TRANSLATION_NAMESPACE_V1;
        this.privacyPolicyLink = useConsentV2 ? undefined : this.buildPrivacyPolicyLink(privacyPolicyUrl);

        this.render();
    }

    open() {
        this.isOpen = true;
        Dom.addClass(BODY_CONSENT_MODAL_CLASSNAME, this.domManager.get('body'));
    }

    close() {
        this.isOpen = false;
        Dom.removeClass(BODY_CONSENT_MODAL_CLASSNAME, this.domManager.get('body'));
        this.destroy();
        this.eventBus.dispatch('modal:close');
    }

    getModalElements() {
        return {
            consentHeaderText: this.element.querySelector('.modal-consent__header'),
            confirmationCheckboxText: this.element.querySelector('.modal-consent__accept .control__label--custom'),
            submitButtonText: this.element.querySelector('.modal-consent__continue span'),
            consentMessage: this.element.querySelector('.modal-consent__message'),
            buttonsContainer: this.element.querySelector('.modal-consent__buttons'),
            privacyPolicyLink: this.element.querySelector('.privacy-policy-link'),
            privacyPolicyLinkMessage: this.element.querySelector('.message__privacy-policy-link'),
            consentCheckbox: this.element.querySelector('.consent-modal-checkbox'),
            consentButton: this.element.querySelector('.consent-modal-button'),
            viewContentButton: this.element.querySelector('.consent-modal-view-content-button'),
        };
    }

    render() {
        this.element = Dom.createElement(MODAL_TEMPLATE);

        this.assembleUI();
        this.addEventListeners();
    }

    assembleUI() {
        this.elements = this.getModalElements();

        this.elements.consentHeaderText.textContent = this.t('welcome');
        this.elements.submitButtonText.textContent = this.t('accept-privacy-policy');
        this.elements.confirmationCheckboxText.innerHTML = this.t(
            this.usesMobileCopy() ? 'mobile.confirmation' : 'web.confirmation',
            this.linkInterpolations(),
        );

        if (this.consentType === CONSENT_TYPE_OPTIONAL) {
            this.buildViewContentButton();

            this.domManager.set('consent-modal-view-content-button', this.elements.buttonsContainer.lastElementChild);
        }

        this.elements.consentMessage.innerHTML = this.assemblePrivacyPolicyMessage();

        if (this.useConsentV2) {
            this.applyLinkHrefs();
        }

        this.domManager.set('consent-modal-button', this.elements.consentButton);
        this.domManager.set('consent-modal-checkbox', this.elements.consentCheckbox);

        this.container.appendChild(this.element);
    }

    /**
     * v2 shows the full web copy on every device (FEAT-9657); the shortened
     * mobile variant only applies to v1.
     */
    usesMobileCopy() {
        return !this.useConsentV2 && isMobileBrowser();
    }

    assemblePrivacyPolicyMessage() {
        const isMobile = this.usesMobileCopy();

        return `${this.buildMessage(isMobile)}${
            this.consentType === CONSENT_TYPE_OPTIONAL ? `${this.buildParagraph('option', isMobile)}` : ''
        }`;
    }

    buildViewContentButton() {
        const viewContentButton = document.createElement('button');

        viewContentButton.classList.add('button--m', 'consent-modal-view-content-button', 'button');

        if (this.useConsentV2) {
            viewContentButton.classList.add('button-tint');
            Dom.addClass('modal-consent__buttons--stacked', this.elements.buttonsContainer);
        } else {
            viewContentButton.classList.add('button-tint--reversed', 'button-outline');
        }
        viewContentButton.innerHTML = this.t('proceed-without-accepting');

        this.elements.buttonsContainer.appendChild(viewContentButton);
    }

    buildMessage(isMobile) {
        const cookieDurationObject = { consentExpiry: this.consentExpiry, ...this.linkInterpolations() };

        return isMobile
            ? `${this.buildParagraph('cookie-tracking', true, cookieDurationObject)}${this.buildParagraph('consent-revoke', true)}`
            : `${this.buildParagraph('content-sharing-purpose', false)}${this.buildParagraph(
                  'cookie-tracking',
                  false,
                  cookieDurationObject,
              )}${this.buildParagraph('account-tracking', false)}${this.buildParagraph(
                  'privacy-policy-reference',
                  false,
                  this.linkInterpolations(),
              )}`;
    }

    buildParagraph(type, mobile, extra) {
        if (!allowedTranslationKeys.find((key) => type === key)) {
            return '';
        }

        return `<p>${this.t(`${mobile ? 'mobile' : 'web'}.${type}`, extra ? extra : undefined)}</p>`;
    }

    t(key, options) {
        return i18next.t(`${this.translationNamespace}.${key}`, options);
    }

    linkInterpolations() {
        return this.useConsentV2 ? {} : { privacyPolicyLink: this.privacyPolicyLink };
    }

    buildPrivacyPolicyLink(privacyPolicyUrl) {
        const label = this.useConsentV2 ? i18next.t('entity.privacy-policy') : 'Privacy Policy';

        return this.buildLink(privacyPolicyUrl, label, 'privacy-policy-link');
    }

    buildLink(url, label, className) {
        const link = document.createElement('a');

        link.classList.add(className);
        link.innerHTML = label;

        const safeUrl = ConsentModal.toSafeUrl(url);

        if (!safeUrl) {
            return link.textContent;
        }

        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.href = safeUrl;

        // Returning only the outerHTML of the element because it is passed to the translation key
        // for it to render within the translated text
        return link.outerHTML;
    }

    /**
     * v2 anchors are part of the translated sentences so translators can position and inflect
     * the linked phrase themselves; only the hrefs are applied from code.
     */
    applyLinkHrefs() {
        [this.elements.consentMessage, this.elements.confirmationCheckboxText].forEach((container) => {
            this.applyHref(container, '.tracking-cookie-link', COOKIE_INFO_URL);
            this.applyHref(container, '.privacy-policy-link', this.privacyPolicyUrl);
        });
    }

    applyHref(container, selector, url) {
        if (!container || !container.querySelectorAll) {
            return;
        }

        const safeUrl = ConsentModal.toSafeUrl(url);

        container.querySelectorAll(selector).forEach((link) => {
            if (!safeUrl) {
                link.removeAttribute('href');

                return;
            }

            link.href = safeUrl;
            link.rel = 'noopener noreferrer';
        });
    }

    /**
     * @param {String} url
     * @returns {String|null} the url when it is an absolute http(s) url, null otherwise
     */
    static toSafeUrl(url) {
        try {
            // Parsing without a base rejects relative urls along with unsupported schemes
            const parsed = new URL(url);

            return ALLOWED_LINK_PROTOCOLS.includes(parsed.protocol) ? parsed.href : null;
        } catch (exception) {
            return null;
        }
    }

    destroy() {
        this.removeEventListeners();
    }

    addEventListeners() {
        Dom.on(this.domManager.get('consent-modal-button'), 'click', this.onSubmitButtonClick);
        Dom.on(this.domManager.get('consent-modal-checkbox'), 'change', this.onCheckboxChange);

        if (this.consentType === CONSENT_TYPE_OPTIONAL) {
            Dom.on(this.domManager.get('consent-modal-view-content-button'), 'click', this.onViewContentButtonClick);
        }
    }

    removeEventListeners() {
        Dom.off(this.domManager.get('consent-modal-button'), 'click', this.onSubmitButtonClick);
        Dom.off(this.domManager.get('consent-modal-checkbox'), 'change', this.onCheckboxChange);

        if (this.consentType === CONSENT_TYPE_OPTIONAL) {
            Dom.off(this.domManager.get('consent-modal-view-content-button'), 'click', this.onViewContentButtonClick);
        }
    }

    onCheckboxChange(event) {
        if (event.target.checked) {
            this.domManager.get('consent-modal-button').removeAttribute('disabled');

            if (this.consentType === CONSENT_TYPE_OPTIONAL) {
                this.domManager.get('consent-modal-view-content-button').style.display = 'none';
            }
        } else {
            this.domManager.get('consent-modal-button').setAttribute('disabled', true);

            if (this.consentType === CONSENT_TYPE_OPTIONAL) {
                this.domManager.get('consent-modal-view-content-button').style.display = 'block';
            }
        }
    }

    onSubmitButtonClick() {
        this.onConsentAccepted();
        this.close();
    }

    onViewContentButtonClick() {
        // In case the user proceeds without giving consent, (Optional Consent),
        // store the timestamp and wait a week to show the modal again
        Storage.set(STORAGE_KEY_CONSENT_HIDDEN, Date.now());
        this.onViewContent();
        this.close();
    }

    static shouldShow(consentRequired, consentType, consentEnabled) {
        if (consentEnabled === false || consentRequired === false) {
            return Promise.resolve(false);
        }
        if (consentType === CONSENT_TYPE_MANDATORY) {
            return Promise.resolve(true);
        }
        if (consentType === CONSENT_TYPE_OPTIONAL) {
            return ConsentModal.consentHidden().then((consentHidden) => !consentHidden);
        }

        return Promise.resolve(false);
    }

    static consentHidden() {
        return Storage.get(STORAGE_KEY_CONSENT_HIDDEN)
            .catch(() => 0)
            .then((value) => {
                const timestamp = parseInt(value, 10);
                const showPolicy = !(Date.now() - timestamp < ONE_WEEK_IN_MS);

                return !showPolicy;
            });
    }
}
